/**
 * Dispatcher Control Page
 * AIDLC Administrative Backend
 *
 * Monitor and manage the chat-triggered agent dispatcher (bridge/agent-dispatcher.js):
 *   - Live status (running/stopped, pid, uptime, model) + start/stop/restart
 *   - Active agent sessions with per-agent kill
 *   - Runtime config editor (model, cost limits, per-agent "working" phrases)
 *   - Live log stream and pending-queue clear
 *
 * Every session the dispatcher starts is a billed Claude call. The page therefore
 * says plainly when the dispatcher is not run from this server (and offers no
 * buttons then), and shows the limits it is enforcing and the last thing they refused.
 * Control, config and the log are for Admin or Operator; the server enforces the same.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useBridge } from '../layout/MainLayout';
import { useAuth } from '../../hooks/useAuth';
import { Card } from '../shared/Card';
import { Button } from '../shared/Button';
import { Badge } from '../shared/Badge';
import {
  getDispatcherState,
  getDispatcherLogs,
  getDispatcherConfig,
  startDispatcher,
  stopDispatcher,
  restartDispatcher,
  updateDispatcherConfig,
  killDispatcherAgent,
  clearDispatcherQueue,
  dispatcherErrorReason,
} from '../../services/dispatcherService';
import {
  CONCURRENT_AGENTS_BOUNDS,
  SESSIONS_PER_HOUR_BOUNDS,
  MAX_PHRASE_LENGTH,
} from '../../types/dispatcher';
import type { DispatcherStatus, DispatcherLogEntry, DispatcherActionResult } from '../../types/dispatcher';

const MAX_LOG_LINES = 500;
/** How close to the bottom of the log counts as "following it", in pixels. */
const LOG_FOLLOW_THRESHOLD_PX = 40;

const START_CONFIRMATION =
  'Start the dispatcher?\n\nAgents will then answer chat messages, and each reply is a billed Claude call.';
const RESTART_CONFIRMATION =
  'Restart the dispatcher?\n\nAgent sessions that are running now will be ended and their replies lost. Each was a billed call, and the message will have to be sent again.';
const STOP_CONFIRMATION =
  'Stop the dispatcher?\n\nAgent sessions that are running now will be ended, and agents will no longer respond to chat.';

interface PhraseRow {
  /** Stable for the life of the row, so editing the agent id does not remount it. */
  rowId: number;
  agentId: string;
  phrase: string;
}

function formatDuration(ms: number | null): string {
  if (ms === null || ms < 0) return '—';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function formatTime(iso: string): string {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? iso : new Date(t).toLocaleTimeString();
}

export function DispatcherControlPage() {
  const { socket } = useBridge();
  const { isAdmin, isOperator } = useAuth();
  const hasRole = isAdmin || isOperator;

  const [status, setStatus] = useState<DispatcherStatus | null>(null);
  const [logs, setLogs] = useState<DispatcherLogEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // Config editor draft state
  const [model, setModel] = useState('');
  const [defaultPhrase, setDefaultPhrase] = useState('');
  const [maxConcurrent, setMaxConcurrent] = useState('');
  const [maxPerHour, setMaxPerHour] = useState('');
  const [phraseRows, setPhraseRows] = useState<PhraseRow[]>([]);
  const nextRowId = useRef(1);

  const logBoxRef = useRef<HTMLDivElement | null>(null);
  // Whether the reader is at the bottom of the log. Read before each new line is
  // drawn, so someone who has scrolled up to read is not dragged back down.
  const followLog = useRef(true);

  const managedHere = status?.managed === 'local';
  const canControl = hasRole && managedHere;

  const loadConfig = useCallback(async () => {
    const cfg = await getDispatcherConfig();
    setModel(cfg.model ?? '');
    setDefaultPhrase(cfg.defaultWorkingPhrase);
    setMaxConcurrent(String(cfg.maxConcurrentAgents));
    setMaxPerHour(String(cfg.maxSessionsPerHour));
    setPhraseRows(Object.entries(cfg.workingPhrases).map(([agentId, phrase]) => ({
      rowId: nextRowId.current++,
      agentId,
      phrase,
    })));
  }, []);

  // Initial load. State is for everyone; the config and the log are only asked
  // for when the server would give them (right role, dispatcher run from here).
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const st = await getDispatcherState();
        if (!live) return;
        setStatus(st);
        if (!hasRole || st.managed !== 'local') return;
        const lg = await getDispatcherLogs(MAX_LOG_LINES);
        if (!live) return;
        setLogs(lg);
        await loadConfig();
      } catch (err) {
        if (live) setError(dispatcherErrorReason(err, 'Failed to load dispatcher state'));
      }
    })();
    return () => { live = false; };
  }, [hasRole, loadConfig]);

  // Real-time updates
  useEffect(() => {
    if (!socket) return;
    const onState = (st: DispatcherStatus) => setStatus(st);
    const onLog = (entry: DispatcherLogEntry) =>
      setLogs((prev) => (prev.some((l) => l.seq === entry.seq && l.ts === entry.ts) ? prev : [...prev, entry].slice(-MAX_LOG_LINES)));
    socket.on('dispatcher:state', onState);
    socket.on('dispatcher:log', onLog);
    return () => {
      socket.off('dispatcher:state', onState);
      socket.off('dispatcher:log', onLog);
    };
  }, [socket]);

  // Keep the newest line in view, without animation (a smooth scroll per line
  // never settles on a busy log) and only while the reader is following it.
  useEffect(() => {
    const box = logBoxRef.current;
    if (box && followLog.current) box.scrollTop = box.scrollHeight;
  }, [logs]);

  const onLogScroll = useCallback(() => {
    const box = logBoxRef.current;
    if (!box) return;
    followLog.current = box.scrollHeight - box.scrollTop - box.clientHeight <= LOG_FOLLOW_THRESHOLD_PX;
  }, []);

  const refreshStatus = useCallback(async () => {
    try {
      setStatus(await getDispatcherState());
    } catch {
      // The last known state stays on screen; the action's own error is already shown.
    }
  }, []);

  /** Run one of start, stop or restart, and say what the server said. */
  const runLifecycle = useCallback(async (
    name: string,
    fn: () => Promise<DispatcherActionResult>,
    done: string,
  ) => {
    setBusy(name);
    setError(null);
    setNotice(null);
    try {
      const result = await fn();
      setStatus(result.status);
      if (!result.ok) {
        setError(result.reason || `${name} was refused`);
      } else if (result.survivors && result.survivors.length > 0) {
        // Done, but not cleanly: a session could not be confirmed stopped. That is a warning, not a success.
        setError(result.reason || `Agent session processes may still be running: pid ${result.survivors.join(', ')}`);
      } else if (result.sessionsVerified === false) {
        // Stopped, but nobody saw the agent sessions go (a forced stop, for one). Not a clean success either.
        setError(result.reason || 'The dispatcher stopped, but whether its agent sessions have gone was not verified.');
      } else {
        const how = result.forced ? ' It did not exit when asked and was ended by force.' : '';
        setNotice(`${done}${how}${result.reason ? ` ${result.reason}.` : ''}`);
      }
    } catch (err) {
      setError(dispatcherErrorReason(err, `${name} failed`));
      await refreshStatus();
    } finally {
      setBusy(null);
    }
  }, [refreshStatus]);

  const handleClearQueue = useCallback(async () => {
    if (!window.confirm('Clear the pending message queue? Agent sessions that are already running are not affected.')) return;
    setBusy('clear-queue');
    setError(null);
    setNotice(null);
    try {
      await clearDispatcherQueue();
      setNotice('Clear-queue request sent. Messages that were waiting will not be picked up.');
    } catch (err) {
      setError(dispatcherErrorReason(err, 'Failed to clear the queue'));
    } finally {
      setBusy(null);
    }
  }, []);

  const handleSaveConfig = useCallback(async () => {
    setBusy('save-config');
    setError(null);
    setNotice(null);
    try {
      const workingPhrases: Record<string, string> = {};
      for (const row of phraseRows) {
        const id = row.agentId.trim();
        if (id && row.phrase.trim()) workingPhrases[id] = row.phrase;
      }
      await updateDispatcherConfig({
        model: model.trim() || null,
        defaultWorkingPhrase: defaultPhrase.trim() ? defaultPhrase : undefined,
        workingPhrases,
        // Sent as typed: the server says exactly what is wrong with a bad number.
        maxConcurrentAgents: Number(maxConcurrent),
        maxSessionsPerHour: Number(maxPerHour),
      });
      setNotice('Config saved. The dispatcher uses it from the next message on.');
      await loadConfig();
    } catch (err) {
      setError(dispatcherErrorReason(err, 'Failed to save config'));
    } finally {
      setBusy(null);
    }
  }, [model, defaultPhrase, maxConcurrent, maxPerHour, phraseRows, loadConfig]);

  const handleKill = useCallback(async (agentId: string, name: string) => {
    if (!window.confirm(`End ${name}'s active session? Its reply will be lost.`)) return;
    setBusy(`kill-${agentId}`);
    setError(null);
    setNotice(null);
    try {
      const result = await killDispatcherAgent(agentId);
      setNotice(result.descendantsChecked === false
        ? `${name}'s session was stopped; child processes could not be listed, so only the session itself was checked.`
        : `${name}'s session was ended, and the dispatcher confirmed its processes are gone.`);
    } catch (err) {
      // Includes a kill that was issued but could not be confirmed: the server says which pids are still running.
      setError(dispatcherErrorReason(err, 'Failed to kill agent'));
      await refreshStatus();
    } finally {
      setBusy(null);
    }
  }, [refreshStatus]);

  const running = status?.running ?? false;
  const badge = status === null
    ? { variant: 'neutral' as const, label: 'Checking…' }
    : status.managed === 'remote'
      ? { variant: 'neutral' as const, label: 'Managed remotely' }
      : status.managed === 'disabled'
        ? { variant: 'neutral' as const, label: 'Not enabled here' }
        : running
          ? { variant: 'success' as const, label: 'Running' }
          : { variant: 'neutral' as const, label: 'Stopped' };

  return (
    <div className="space-y-6">
      {/* Header / status */}
      <Card
        title="Dispatcher Control"
        subtitle="Chat-triggered agent dispatcher (bridge/agent-dispatcher.js)"
        action={<Badge variant={badge.variant} dot>{badge.label}</Badge>}
      >
        {status !== null && !managedHere && (
          <div role="status" className="mb-4 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">
            <span className="font-semibold">
              {status.managed === 'remote' ? 'The dispatcher is managed remotely.' : 'Dispatcher control is not enabled on this server.'}
            </span>{' '}
            {status.managed === 'remote'
              ? 'It runs on another machine, so it cannot be started, stopped or configured from this page, and its state is not shown here.'
              : 'Nothing can be started, stopped or configured from this page. An administrator of the server turns this on.'}
          </div>
        )}
        {error && (
          <div role="alert" className="mb-4 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        {notice && (
          <div role="status" className="mb-4 rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">
            {notice}
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
          <Stat label="PID" value={status?.pid ? String(status.pid) : '—'} />
          <Stat label="Uptime" value={formatDuration(status?.uptimeMs ?? null)} />
          <Stat label="Model" value={running ? status?.model ?? 'default' : '—'} />
          <Stat
            label="Heartbeat"
            value={status?.heartbeatAgeMs != null ? `${Math.round(status.heartbeatAgeMs / 1000)}s ago` : '—'}
          />
          <Stat
            label="Sessions at once"
            value={status?.limits ? `${status.activeAgents.length} of ${status.limits.maxConcurrentAgents}` : '—'}
          />
          <Stat
            label="Sessions this hour"
            value={status?.limits && status.sessionsLastHour !== null
              ? `${status.sessionsLastHour} of ${status.limits.maxSessionsPerHour}`
              : '—'}
          />
          <Stat label="Refused by limits" value={running ? String(status?.refusedCount ?? 0) : '—'} />
        </div>

        {status?.lastTermination && !status.lastTermination.verified && (
          <div role="alert" className="mb-4 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            <span className="font-semibold">
              {status.lastTermination.scope === 'kill'
                ? `The kill of ${status.lastTermination.agentId ?? 'an agent'} at ${formatTime(status.lastTermination.at)} could not be confirmed.`
                : `The dispatcher stopped at ${formatTime(status.lastTermination.at)}, but not every agent session could be confirmed stopped.`}
            </span>{' '}
            {status.lastTermination.survivors.length > 0
              ? `Still running when last checked: pid ${status.lastTermination.survivors.join(', ')}.`
              : 'Which processes were left is not known.'}{' '}
            A session that is still running is still being billed. End it on the server.
          </div>
        )}

        {status?.lastTermination && status.lastTermination.verified && !status.lastTermination.descendantsChecked && status.lastTermination.sessions > 0 && (
          <div role="status" className="mb-4 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">
            <span className="font-semibold">
              {status.lastTermination.scope === 'kill'
                ? `The session of ${status.lastTermination.agentId ?? 'an agent'} was stopped at ${formatTime(status.lastTermination.at)}.`
                : `The dispatcher's sessions were stopped at ${formatTime(status.lastTermination.at)}.`}
            </span>{' '}
            Child processes could not be listed, so only the session itself was checked.
          </div>
        )}

        {status?.lastRefusal && (
          <div className="mb-4 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">
            <span className="font-semibold">A limit refused work at {formatTime(status.lastRefusal.at)}:</span>{' '}
            {status.lastRefusal.reason} (agent {status.lastRefusal.agentId}). Nothing was started and nothing was billed for it.
          </div>
        )}

        {/* No buttons at all when this server does not run the dispatcher: a
            disabled Start still reads as "this can be started from here". */}
        {managedHere && (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="primary"
            size="sm"
            disabled={!canControl || running || busy !== null}
            isLoading={busy === 'start'}
            onClick={() => {
              if (window.confirm(START_CONFIRMATION)) runLifecycle('start', startDispatcher, 'Dispatcher started.');
            }}
          >
            Start
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!canControl || !running || busy !== null}
            isLoading={busy === 'restart'}
            onClick={() => {
              if (window.confirm(RESTART_CONFIRMATION)) runLifecycle('restart', restartDispatcher, 'Dispatcher restarted.');
            }}
          >
            Restart
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={!canControl || !running || busy !== null}
            isLoading={busy === 'stop'}
            onClick={() => {
              if (window.confirm(STOP_CONFIRMATION)) runLifecycle('stop', stopDispatcher, 'Dispatcher stopped.');
            }}
          >
            Stop
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={!canControl || !running || busy !== null}
            isLoading={busy === 'clear-queue'}
            onClick={handleClearQueue}
          >
            Clear Queue
          </Button>
        </div>
        )}
        {status !== null && managedHere && !hasRole && (
          <p className="mt-2 text-xs text-gray-500">
            Read-only: the Admin or Operator role is required to control the dispatcher, change its config or read its log.
          </p>
        )}
      </Card>

      {/* Active agents */}
      <Card title="Active Agents" subtitle={`${status?.activeAgents.length ?? 0} responding now`}>
        {status && status.activeAgents.length > 0 ? (
          <ul className="divide-y divide-gray-100">
            {status.activeAgents.map((a) => (
              <li key={a.agentId} className="flex items-center justify-between py-2">
                <div>
                  <div className="text-sm font-medium text-ldaja-black">
                    {a.name}
                    {a.stuck && <span className="ml-2 text-xs font-semibold text-red-700">kill not confirmed, still running</span>}
                  </div>
                  <div className="text-xs text-gray-500">
                    {a.subject ?? 'Working'} · {a.source ?? '—'} · {formatDuration(a.startedAt ? Date.now() - Date.parse(a.startedAt) : null)}
                  </div>
                </div>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={!canControl || busy !== null}
                  isLoading={busy === `kill-${a.agentId}`}
                  onClick={() => handleKill(a.agentId, a.name)}
                >
                  Kill
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500">No agents currently responding.</p>
        )}
      </Card>

      {/* Config editor */}
      {canControl && (
        <Card
          title="Runtime Config"
          subtitle="Model, cost limits and per-agent working phrases. Applied live, no restart needed. Admin or Operator."
          action={
            <Button
              variant="primary"
              size="sm"
              disabled={busy !== null}
              isLoading={busy === 'save-config'}
              onClick={handleSaveConfig}
            >
              Save Config
            </Button>
          }
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Model override (blank = default)">
                <input
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="e.g. haiku, sonnet"
                  maxLength={64}
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                />
              </Field>
              <Field label="Default working phrase">
                <input
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  maxLength={MAX_PHRASE_LENGTH}
                  value={defaultPhrase}
                  onChange={(e) => setDefaultPhrase(e.target.value)}
                />
              </Field>
              <Field label={`Agent sessions at once (${CONCURRENT_AGENTS_BOUNDS.min} to ${CONCURRENT_AGENTS_BOUNDS.max})`}>
                <input
                  type="number"
                  step={1}
                  min={CONCURRENT_AGENTS_BOUNDS.min}
                  max={CONCURRENT_AGENTS_BOUNDS.max}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  value={maxConcurrent}
                  onChange={(e) => setMaxConcurrent(e.target.value)}
                />
              </Field>
              <Field label={`Sessions started per hour (${SESSIONS_PER_HOUR_BOUNDS.min} to ${SESSIONS_PER_HOUR_BOUNDS.max})`}>
                <input
                  type="number"
                  step={1}
                  min={SESSIONS_PER_HOUR_BOUNDS.min}
                  max={SESSIONS_PER_HOUR_BOUNDS.max}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  value={maxPerHour}
                  onChange={(e) => setMaxPerHour(e.target.value)}
                />
              </Field>
            </div>
            <p className="text-xs text-gray-500">
              Each session is a billed Claude call. When a limit is reached the dispatcher starts nothing more, tells the
              sender, and shows the refusal above. An agent still runs one session at a time whatever these say.
            </p>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-600">Per-agent working phrases ("Clauding…" style)</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPhraseRows((prev) => [...prev, { rowId: nextRowId.current++, agentId: '', phrase: '' }])}
                >
                  + Add
                </Button>
              </div>
              <div className="space-y-2">
                {phraseRows.length === 0 && <p className="text-sm text-gray-500">No custom phrases.</p>}
                {phraseRows.map((row) => (
                  <div key={row.rowId} className="flex gap-2">
                    <input
                      className="w-32 border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                      placeholder="agentId"
                      aria-label="Agent id"
                      maxLength={32}
                      value={row.agentId}
                      onChange={(e) =>
                        setPhraseRows((prev) => prev.map((r) => (r.rowId === row.rowId ? { ...r, agentId: e.target.value } : r)))
                      }
                    />
                    <input
                      className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                      placeholder="Toe Drag Releasing!"
                      aria-label="Working phrase"
                      maxLength={MAX_PHRASE_LENGTH}
                      value={row.phrase}
                      onChange={(e) =>
                        setPhraseRows((prev) => prev.map((r) => (r.rowId === row.rowId ? { ...r, phrase: e.target.value } : r)))
                      }
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPhraseRows((prev) => prev.filter((r) => r.rowId !== row.rowId))}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Live logs */}
      {canControl && (
        <Card title="Live Logs" subtitle="Dispatcher stdout/stderr (most recent 500 lines). Admin or Operator." noPadding>
          <div
            ref={logBoxRef}
            onScroll={onLogScroll}
            className="h-72 overflow-y-auto bg-gray-900 rounded-b-lg p-3 font-mono text-xs leading-relaxed"
          >
            {logs.length === 0 ? (
              <div className="text-gray-500">No log output captured yet. Logs are captured for dispatchers started from this panel.</div>
            ) : (
              logs.map((l) => (
                <div
                  key={`${l.ts}#${l.seq}`}
                  className={
                    l.type === 'stderr' ? 'text-yellow-300' : l.type === 'system' ? 'text-ldaja-cyan' : 'text-gray-200'
                  }
                >
                  <span className="text-gray-500">{l.ts.slice(11, 19)} </span>
                  {l.line}
                </div>
              ))
            )}
          </div>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-gray-50 px-3 py-2">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm font-semibold text-ldaja-black truncate">{value}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
      {children}
    </label>
  );
}
